import { Fragment } from "react";

import { Box, Divider, Stack } from "@mui/material";
import { ExpandCollapseButton, Navigation, NavigationDrawer } from "@shared";

import { AppRoutes } from "@/router/AppRoutes";
import { useTranslation } from "@/translations";

import { NavigationActions } from "./components/navigation-actions/NavigationActions";
import { useLayoutNavigation } from "./hooks/useLayoutNavigation";

interface Props {
  isNavigationCollapsed: boolean;
  onToggleNavigationCollapse: (collapsed: boolean) => void;
  navigationWidth: number;
  isDropZoneOpen: boolean;
  onDropZoneToggle: () => void;
}

export function LayoutNavigation({
  isNavigationCollapsed,
  onToggleNavigationCollapse,
  navigationWidth,
  isDropZoneOpen,
  onDropZoneToggle,
}: Props) {
  const { t } = useTranslation();
  const { navigationGroups, statisticsNavigationItems, settingsNavigationItems } = useLayoutNavigation();

  return (
    <NavigationDrawer
      homeTo={AppRoutes.Default}
      ariaLabel={t("layout.header.mainNavigation")}
      logoAlt={t("layout.header.startPage")}
      isNavigationCollapsed={isNavigationCollapsed}
      navigationWidth={navigationWidth}
    >
      <Stack justifyContent={"space-between"} gap={2}>
        {navigationGroups.map((group, index) => (
          <Fragment key={index}>
            {index > 0 && <Divider />}
            <Navigation navigationItems={group} isCollapsed={isNavigationCollapsed} />
          </Fragment>
        ))}
        <NavigationActions
          isCollapsed={isNavigationCollapsed}
          isDropZoneOpen={isDropZoneOpen}
          onDropZoneToggle={onDropZoneToggle}
          aria-label={t("layout.header.actionsMenu")}
        />
      </Stack>

      <Stack gap={2}>
        <Navigation navigationItems={statisticsNavigationItems} isCollapsed={isNavigationCollapsed} />
        <Divider />
        <Stack direction={isNavigationCollapsed ? "column" : "row"} alignItems={"center"}>
          <Box flexGrow={1} minWidth={0} width={isNavigationCollapsed ? "100%" : undefined}>
            <Navigation navigationItems={settingsNavigationItems} isCollapsed={isNavigationCollapsed} />
          </Box>
          <ExpandCollapseButton
            isNavigationCollapsed={isNavigationCollapsed}
            onToggleNavigationCollapse={onToggleNavigationCollapse}
          />
        </Stack>
      </Stack>
    </NavigationDrawer>
  );
}
